import React, { useState, useEffect, useMemo } from 'react';
import { QueryClient, QueryClientProvider, useQuery, useQueries } from 'react-query';
import ProductCard from '../ProductCard';
const queryClient = new QueryClient();

interface CartUpsellsProps {
  ctx: any;
  el: HTMLElement;
  [key: string]: any;
}



interface CartContents {
  items: Array<{
    product_id: number;
    // Add other cart item properties as needed
  }>;
}

// Helper function to fetch recommendations for a single product
const fetchRecommendations = async (productId: number) => {
  if (!productId) return { products: [] };
  
  const response = await fetch(
    `/recommendations/products.json?product_id=${productId}&limit=2&intent=related`
  );
  return response.json();
};

// Add this custom hook at the top level of the file, after the imports
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const CartUpsells = (props: CartUpsellsProps) => {
  
  // Pull cart contents from Shopify API
  const { data: cartContents, isLoading: isLoadingCart } = useQuery<CartContents>({
    queryKey: ['cart-contents'],
    queryFn: () => fetch('/cart.js').then(res => res.json()),
  });

  // console.log('cartContents', cartContents);

  // Create parallel queries for each product in cart
  const recommendationQueries = useQueries(
    (cartContents?.items || []).map(item => ({
      queryKey: ['recommendations', item.product_id],
      queryFn: () => fetchRecommendations(item.product_id),
      enabled: !!item.product_id,
    }))
  );

  // Combine all recommendations and limit to 6 total, excluding products already in cart
  const combinedRecommendations = useMemo(() => {
    if (recommendationQueries.some(query => query.isLoading)) return [];

    // Get array of product IDs currently in cart
    const cartProductIds = new Set(cartContents?.items?.map(item => item.product_id) || []);

    const allProducts = recommendationQueries
      .filter(query => query.data?.products)
      .flatMap(query => query.data.products)
      // Remove duplicates and products already in cart
      .filter((product, index, self) => 
        index === self.findIndex(p => p.id === product.id) && // Remove duplicates
        !cartProductIds.has(product.id) // Exclude products in cart
      )
      // Limit to 6 products total
      .slice(0, 6);

    return allProducts;
  }, [recommendationQueries, cartContents?.items]);

  if (isLoadingCart || recommendationQueries.some(query => query.isLoading)) {
    return <div>Loading...</div>;
  }

  if (!cartContents?.items?.length) {
    return null; // Don't show anything if cart is empty
  }

  return (
    <div className={`cart-upsells ${combinedRecommendations.length > 0 ? 'active' : ''}`}>
      {combinedRecommendations.length > 0 && (
        <>
          <h3 className="cart-upsells__title">You might also like</h3>
          <div className="cart-upsells__grid">
            {combinedRecommendations.map((product) => (
              <ProductCard key={product.id} product={product} showQuickAdd={true} cartContents={cartContents} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const CartUpsellsProvider = (props: CartUpsellsProps) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    props.el.remove();
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <CartUpsells {...props} />
    </QueryClientProvider>
  );
}

export default CartUpsellsProvider;